import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { StorageEnum } from '~/utils/enum';

export function notify(type: string, message: string) {
  let option = { toastId: 'bor-toast' };
  if (type === 'error') {
    return toast.error(message, option);
  }

  if (type === 'success') {
    return toast.success(message, option);
  }

  if (type === 'warning') {
    return toast.warning(message, option);
  }

  return toast(message, option);
}

export function catchError(e: unknown, handleError: (errorMessage: string) => void) {
  if (typeof e === 'string') {
    handleError(e);
  } else if (e instanceof AxiosError) {
    handleError(e?.response?.data?.message);
  } else if (e instanceof Error) {
    handleError(e.message);
  }
}

export function handleApiError(
  e: unknown,
  setFieldError?: (field: string, message: string | undefined) => void,
  isNotify: boolean = true
) {
  if (isNotify) {
    catchError(e, (message) => {
      notify('error', message || 'Something went wrong.');
    });
  }

  if (setFieldError && e instanceof AxiosError && e?.response?.data?.data) {
    for (let data of e.response.data.data) {
      setFieldError(data.field, data.message);
    }
  }
}

export function convertTextTimeToNumber(value: string) {
  const timeArray: Array<string> = value.toLowerCase().split('h');
  let hour: number = 0;
  let minute: number = 0;

  const time1st: number = +timeArray[0].replace(/[^0-9]+/g, '');

  // Case only has minutes
  if (timeArray[0].indexOf('m') > -1) {
    return time1st;
  }

  hour = time1st * 60;
  // Case has minutes
  if (timeArray[1]) {
    minute = +timeArray[1].replace(/[^0-9]+/g, '');
  }

  return hour + minute;
}

export function convertNumberToTime(number: number) {
  const hours = Math.floor(number / 3600);
  const minutes = Math.floor((number - hours * 3600) / 60);
  const seconds = number - hours * 3600 - minutes * 60;

  let hoursText = hours.toString();
  let minutesText = minutes.toString();
  let secondsText = seconds.toString();

  if (hours < 10) hoursText = '0' + hoursText;
  if (minutes < 10) minutesText = '0' + minutesText;
  if (seconds < 10) secondsText = '0' + secondsText;

  return hoursText + ':' + minutesText + ':' + secondsText;
}

export function getTimezone() {
  let timezone = 'UTC';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    console.warn('Cannot get timezone');
  }

  return timezone;
}

/**
 * React doesn't work fine with null, undefined and string
 *
 * cast value from boolean true/false to string "true"/"false"
 * cast null and undefined to ""
 *
 * @param value
 * @returns
 */
export function cast(value: any) {
  if (typeof value === 'boolean') return String(value);

  return value ?? '';
}

/**
 * Get userId from JWT token
 * @returns userId string or null if not found
 */
export function getUserIdFromToken(): string | null {
  try {
    const token = localStorage.getItem(StorageEnum.ACCESS_TOKEN);
    if (!token) return null;
    
    // JWT token format: header.payload.signature
    const payload = token.split('.')[1];
    if (!payload) return null;
    
    // Decode base64 payload
    const decodedPayload = JSON.parse(atob(payload));
    return decodedPayload.userId || decodedPayload.sub || decodedPayload.id || null;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
}

/** "$1,234.50" / "-$1,234.50". Accepts API decimal strings. */
export function formatMoney(value: number | string | null | undefined) {
  const n = Number(value) || 0;
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${Math.round(n * 100) < 0 ? '-' : ''}$${abs}`;
}

/**
 * Date-only fields (e.g. budget_date) are stored as `timestamp` and come back as UTC midnight
 * ("2026-02-19T00:00:00.000Z"). dayjs() on that shifts to the previous day in US time zones,
 * so keep only the "YYYY-MM-DD" part.
 */
export function toDateOnly(value: string | null | undefined) {
  return value ? String(value).slice(0, 10) : '';
}

/** Save a blob response (e.g. a generated PDF) as a file. */
export function downloadBlob(data: Blob, fileName: string) {
  const url = window.URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
